// 상품 리스트 & 대량 상품 등록에서 공통으로 사용하는 상품 정보 디스플레이 관련 로직을 정의합니다.
import { i18n } from '@/main';
import {OptColumn} from '@/pro/types';
import {emptyValueFormatter, periodFormatter} from '@/pro/utils/grid/formatterUtils';
import {productNameConvertor} from '@/pro/utils/common';
import {Row} from '@/pro/types/tui-grid';
import {DisplayCategoryDetailOpenerRenderer} from '@/pro/utils/grid/rendererUtils';
import {SALE_METHOD_TYPES, SaleMethodType} from '@/pro/components/product/basic/SearchOptionTypes';
import {formatCurrency} from '@/pro/utils/numberFormat';
import {getSupplyPriceColumn} from '@/pro/components/product/common/girdColumns/ProductSupplyPrice';

// 상품번호
export const getProductNoColumn = (name: 'mallProductNo' | 'productNo' = 'mallProductNo'): OptColumn => ({
  header: 'PRO_PRODUCT_NUMBER',
  name,
  align: 'center',
  minWidth: 110,
  formatter: ({ row }) => {
    return `<a href="/product/edit/${row.mallProductNo}?planType=${row.mallServicePlan}" target="_blank" class="underline">${row.mallProductNo}</a>`;
  },
});

// 상품명
export const getProductNameColumn = (useConvertor = true): OptColumn => ({
  header: 'PRO_PRODUCT_NAME',
  name: 'productName',
  align: 'left',
  minWidth: 200,
  formatter: ({ value, row }): string => {
    if (!useConvertor) return value.toString();
    return productNameConvertor(value, row);
  },
});

// 상품관리코드
export const getProductManagementCdColumn = (
  name: 'managementCode' | 'productManagementCd' = 'productManagementCd',
): OptColumn => ({
  header: 'PRO_PRODUCT_PRODUCT_MANAGEMENT_CODE',
  name,
  align: 'center',
  minWidth: 120,
  formatter: ({ value }) => emptyValueFormatter(value),
});

// 판매상태
export const getSaleStatusColumn = (name: 'saleStatus' | 'saleStatusType' = 'saleStatusType'): OptColumn => ({
  header: 'PRO_PRODUCT_STATUS_SALE_STATUS',
  name,
  align: 'center',
  minWidth: 100,
  formatter: ({ value, row }): string => {
    if (row.applyStatusType === 'REGISTRATION_READY') {
      return '-';
    }
    const applyStatus = i18n.t(`PRO_PRODUCT_STATUS_SALE_${value}`).toString();

    if (name !== 'saleStatus' && value === 'ON_SALE' && row.salePeriod['periodType']) {
      const period = i18n.t(`PRO_PRODUCT_STATUS_SALE_${row.salePeriod['periodType']}`).toString();
      return applyStatus + `(${period})`;
    }

    return applyStatus;
  },
});

const getPeriodForDeliveryTemplate = (row: Row): string => {
  const isPeriod = row.saleStartDate && row.saleEndDate;
  if (!isPeriod) return '-';
  const start = row.saleStartDate.toString().split(' ')[0];
  const end = row.saleEndDate.toString().split(' ')[0];
  return `${start} ~ ${end}`;
};

//판매기간
export const getSalePeriodColumn = (minWidth = 250, type = ''): OptColumn => ({
  header: 'PRO_PRODUCT_SALE_PERIOD',
  name: 'salePeriod',
  align: 'center',
  minWidth,
  formatter: ({ row }: any): string => {
    if (type === 'salePeriodForDeliveryTemplate') {
      return getPeriodForDeliveryTemplate(row);
    }
    const isPeriod = row.salePeriod && row.salePeriod.startYmdt && row.salePeriod.endYmdt;
    return isPeriod ? periodFormatter(row.salePeriod) : '-';
  },
});

//전시카테고리
export const getDisplayCategoryNamesColumn = (minWidth = 300): OptColumn => ({
  header: 'PRO_PRODUCT_DISPLAY_CATEGORY',
  name: 'displayCategoryNames',
  align: 'left',
  minWidth,
  // TODO: 추후 개선
  renderer: {
    type: DisplayCategoryDetailOpenerRenderer,
  },
});

//판매가
export const getSalePriceColumn = (minWidth = 80): OptColumn => ({
  header: 'PRO_PRODUCT_SALE_PRICE',
  name: 'salePrice',
  align: 'center',
  minWidth,
  formatter: ({ value }) => formatCurrency(value as number),
});

//즉시할인
export const getImmediateDiscountAmountColumn = (minWidth = 80): OptColumn => ({
  header: 'PRO_PRODUCT_IMMEDIATE_DISCOUNT',
  name: 'immediateDiscountInfo',
  align: 'center',
  minWidth,
  formatter: ({ value }: any) => {// eslint-disable-line
    const percent = value?.unitType === 'PERCENT';
    const currency = formatCurrency(value.amount as number);
    return percent ? currency + '%' : currency;
  },
});

//즉시할인가
export const getAppliedImmediateDiscountPriceColumn = (minWidth = 80): OptColumn => ({
  header: 'PRO_PRODUCT_IMMEDIATE_DISCOUNT_PRICE',
  name: 'appliedImmediateDiscountPrice',
  align: 'center',
  minWidth,
  formatter: ({ value }) => formatCurrency(value as number),
});

//옵션가
export const getOptionColumn = (minWidth = 80): OptColumn => ({
  header: 'PRO_PRODUCT_OPTION',
  name: 'hasOption',
  align: 'center',
  minWidth,
  formatter: ({ row, value }) => {
    if (row.optionType === 'MAPPING') return '-';
    return value ? window.$t('PRO_PRODUCT_ICON_USE') : window.$t('PRO_PRODUCT_ICON_NOT_USE');
  },
});

//프로모션 적용
export const getPromotionColumn = (minWidth = 100): OptColumn => ({
  header: 'PRO_PRODUCT_APPLY_PROMOTION',
  name: 'canPromotion',
  align: 'center',
  minWidth,
  formatter: ({ row, value }) => {
    if (row.applyStatusType === 'REGISTRATION_READY') return '-';
    return value ? 'Y' : 'N';
  },
});

//담당자
export const getAdminNameColumn = (minWidth = 100): OptColumn => ({
  header: 'PRO_PRODUCT_HANDLER',
  name: 'adminName',
  align: 'center',
  minWidth,
});

//판매방식
export const getSaleMethodTypeColumn = (minWidth = 100): OptColumn => ({
  header: 'PRO_PRODUCT_SALES_METHOD',
  name: 'saleMethodType',
  align: 'center',
  minWidth,
  formatter: ({ value }) => {
    if (!SALE_METHOD_TYPES.includes(value as SaleMethodType)) return;
    return window.$t(`PRO_PRODUCT_PRODUCT_${value}`);
  },
});

//수수료율
export const getCommissionColumn = (minWidth = 100): OptColumn => ({
  header: 'PRO_PRODUCT_COMMISSION_RATE',
  name: 'commission',
  align: 'center',
  minWidth,
  formatter: ({ row, value }: any) => {// eslint-disable-line
    if (row.saleMethodType === 'PURCHASE' || row.commission.type === 'PURCHASE_PRICE') {
      return '-';
    }
    return `${value.rate}%`;
  },
});

//매입가
export const getPurchasePriceColumn = (minWidth = 100): OptColumn => ({
  header: 'PRO_PRODUCT_PURCHASE_PRICE',
  name: 'purchasePrice',
  align: 'center',
  minWidth,
  formatter: ({ row, value }) => {
    if (row.saleMethodType === 'CONSIGNMENT') return '-';
    return formatCurrency(value as number);
  },
});

//배송여부
export const getDeliverableColumn = (minWidth = 100): OptColumn => ({
  header: 'PRO_PRODUCT_DELIVERABLE',
  name: 'deliverable',
  align: 'center',
  minWidth,
  formatter: ({ value }) =>
    value ? window.$t('PRO_PRODUCT_PRIORITY_DELIVERY') : window.$t('PRO_PRODUCT_PRIORITY_NO_DELIVERY'),
});

//배송비유형
export const getDeliveryFeeTypeColumn = (minWidth = 130): OptColumn => ({
  header: 'PRO_PRODUCT_DELIVERY_FEE_TYPE',
  name: 'deliveryFeeType',
  align: 'center',
  minWidth,
  formatter: ({ value }) => {
    return value ? i18n.t(`PRO_PRODUCT_PRODUCT_${value}`).toString() : '-';
  },
});

//묶음배송 여부
export const getShippingCombinableColumn = (
  minWidth = 100,
  name: 'shippingCombinable' | 'bundleDeliveryAvailable' = 'shippingCombinable',
): OptColumn => ({
  header: name === 'bundleDeliveryAvailable' ? 'PRO_PRODUCT_POSSIBLE_SHIPPING_COMBINABLE' : 'PRO_PRODUCT_SHIPPING_COMBINABLE',
  name,
  align: 'center',
  minWidth,
  formatter: ({ value }) => {
    return value === true ? i18n.t('PRO_COMMON_MAY').toString() : i18n.t('PRO_COMMON_UNABLE').toString();
  },
});

//브랜드
export const getBrandNameColumn = (minWidth = 100): OptColumn => ({
  header: 'PRO_PRODUCT_BRAND',
  name: 'brandName',
  align: 'center',
  minWidth,
  formatter: ({ value }) => emptyValueFormatter(value),
});

//판매가, 즉시할인, 즉시할인가
export const getDiscountColumns = (): OptColumn[] => {
  return [getSalePriceColumn(), getImmediateDiscountAmountColumn(), getAppliedImmediateDiscountPriceColumn()];
};

//프로모션적용, 담당자, 판매방식, 수수료율, 공급가, 매입가
export const getCommonGridColumns = (): OptColumn[] => {
  return [
    getPromotionColumn(),
    getAdminNameColumn(),
    getSaleMethodTypeColumn(),
    getCommissionColumn(),
    getSupplyPriceColumn(),
    getPurchasePriceColumn(),
  ];
};

//배송여부, 배송비유형, 묶음배송 여부
export const getDeliveryColumns = (): OptColumn[] => {
  return [getDeliverableColumn(), getDeliveryFeeTypeColumn(), getShippingCombinableColumn()];
};

// 쇼핑몰명, 상품번호 상품명
export const getProductColumns = (): OptColumn[] => {
  return [
    {
      header: 'COMMON_MALL',
      name: 'mallName',
      align: 'center',
      minWidth: 80,
    },
    getProductNoColumn(),
    getProductNameColumn(),
  ];
};

// 상품 적립률
export const getAccumulationRateColumn = (minWidth = 80): OptColumn => ({
  header: 'PRO_PRODUCT_ACCUMULATION_RATE',
  name: 'accumulationRate',
  align: 'center',
  minWidth,
  formatter: ({ value }) => {
    return value ? `${value}%` : i18n.t('COMMON_NOT_USED').toString();
  },
});
